import type { UserInfo } from '@/types/auth';

/**
 * Decodes a JWT token without verification
 * @param token - The JWT token to decode
 * @returns The decoded token payload
 * @throws Error if token format is invalid
 */
export function decodeJWT(token: string): UserInfo {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const payload = parts[1];
    if (!payload) {
      throw new Error('Invalid JWT payload');
    }

    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded as UserInfo;
  } catch (error) {
    throw new Error(
      `Failed to decode JWT: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Checks if a JWT token is expired
 * @param token - The JWT token to check
 * @returns True if token is expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  try {
    const decoded = decodeJWT(token);
    if (!decoded.exp) {
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
}

/**
 * Gets the expiration time of a JWT token in seconds
 * @param token - The JWT token
 * @returns The expiration time in seconds since epoch, or null if not available
 */
export function getTokenExpiration(token: string): number | null {
  try {
    const decoded = decodeJWT(token);
    return decoded.exp ?? null;
  } catch {
    return null;
  }
}
